// layoutResolution — Classic ⇄ Modern の「初回どちらを出すか」「トグルを出すか」を
// 純関数で決める presentation 層ロジック.
//
// 背景 (docs/_wip/modern-layout.md §9.4 / §9.4.1、consult 2026-06-01 / 2026-06-08):
//   - 切替粒度は per-user。スライス1 (`feature/modern-layout-shell`) の確定スコープは
//     「明示トグルのみ localStorage 永続」の **最小チェーン** のみ:
//         ユーザ設定 (localStorage) > グローバル既定 (= Modern)
//     部屋デフォルト・`?layout=` URL ヒントは後続スライス送り (ここに足さない)。
//   - デバイスルール (§9.4 / §9.4.1 ④):
//       - PC + タブレット (iPad 等) = per-user 選択 (Classic / Modern 両方可)。
//       - **スマホ (小画面) = Modern 固定**。Classic の PC 前提レイアウトが小画面に
//         収まらないため。スマホでは切替トグル自体を非表示にする (= 選べないものを
//         見せない)。`?layout=classic` でスマホ来訪も無視して Modern (後続スライスで
//         URL ヒントを足す際もこのデバイスゲートが優先)。
//
// なぜ純関数に切り出すか:
//   - vitest が node 環境 (DOM mount 不可) なので、UI を mount せずに
//     「スマホ幅でトグル非表示・Modern 固定」「トグルは stored 値を優先」を
//     単体テストで担保するため (= 配線は ModernLayout / LayoutToggle の smoke test、
//     決定ロジックはここで網羅)。

import type { DeviceKind } from '@oekaki/protocol';

import type { LayoutKind } from '../../infrastructure/layoutPreference.js';

/**
 * Classic レイアウトが収まる最小幅 (px). この幅未満を「スマホ (小画面)」とみなし、
 * Modern 固定 + 切替トグル非表示にする (§9.4.1 ④)。
 *
 * 仮決めの根拠 (実測ベース、コメントに残す = §9.4.1 「breakpoint はシェル実装時に
 * 実測で確定」):
 *   - Classic の grid は最小で:
 *       左カラム  60px (* sidebarZoom、最小段 xxsmall=0.72 でも ~43px)
 *       中央 canvas minmax(0,1fr) — canvas 自体は overflow-auto でスクロール可だが
 *                   描画領域として実用的に見えるには最低 ~360px は欲しい
 *       右 member  既定 ~140px (splitter で 80px まで縮められる)
 *     + gap/padding (8px * 数箇所) ≒ ~40px
 *     合計の実用下限 ≒ 43 + 360 + 80 + 40 ≒ 520px 付近。
 *   - 主要スマホ縦の CSS 幅は 320〜430px (iPhone SE 320 / 標準 390〜393 /
 *     Plus/Max 428〜430)。横向きでも 600〜932px。
 *   - 上記から **640px** を境界に採る: 一般的なスマホ縦 (〜430px) は確実に
 *     Modern 固定に倒れ、小型タブレット縦 (768〜) は per-user 選択に入る。
 *     640px は Tailwind の `sm` breakpoint とも一致し、後で CSS と揃えやすい。
 *   - 実機 fb (2026-07-02): スマホ横持ち (幅 844 等) が幅のみ判定では PC 扱いに
 *     なる問題が出たため、判定は幅単独でなく `isPhoneViewport` (幅 ‖ coarse
 *     pointer × 短辺) に拡張した。境界値 640 自体は据え置き。
 */
export const CLASSIC_MIN_VIEWPORT_PX = 640;

/**
 * 「タッチ主体デバイスか」を判定する media query。primary pointer が coarse (= 指)
 * のデバイスのみ true になる。タッチ対応ノート PC は primary が fine (= マウス/
 * トラックパッド) なので誤爆しない。
 */
export const COARSE_POINTER_MEDIA_QUERY = '(pointer: coarse)';

export interface PhoneViewportInput {
  /** CSS px (= `window.innerWidth`). */
  readonly width: number;
  /** CSS px (= `window.innerHeight`). */
  readonly height: number;
  /** `matchMedia('(pointer: coarse)').matches` (= タッチ主体デバイス). */
  readonly coarsePointer: boolean;
}

/**
 * 「スマホ (= Modern フォンシェル固定)」かを判定する純関数.
 *
 *   isPhoneViewport = 幅 < 640 ‖ (タッチ主体デバイス かつ 短辺 < 640)
 *
 * 幅のみ判定 (`width < 640`) だと**スマホ横持ち** (例: 844×390) が PC 扱いになり
 * デスクトップシェルに化ける実機 fb があったため、coarse pointer + 短辺で拡張した
 * (M1 フォローアップ 2026-07-02、modern-phone-shell-2026-07-02.md §3.2/§4 の
 * 「breakpoint + `pointer: coarse` 併用の要否」に対応)。期待挙動:
 *
 *   - スマホ縦 390×844 (coarse)  → true  (幅ルール、従来どおり)
 *   - スマホ横 844×390 (coarse)  → true  (短辺 390 < 640、**今回の拡張**)
 *   - iPad 1024×768 / 768×1024 (coarse) → false (短辺 768 ≥ 640 = per-user 選択の
 *     タブレット扱いを壊さない、modern-layout.md §9.4 デバイスルール)
 *   - PC 細窓 500×900 (fine)     → true  (幅ルール、従来どおり)
 *   - PC 通常 (fine)             → false (幅 640 境界含め完全不変)
 *   - タッチモニタ PC 1920×1080 (coarse) → false (短辺 1080 ≥ 640)
 */
export const isPhoneViewport = ({ width, height, coarsePointer }: PhoneViewportInput): boolean =>
  width < CLASSIC_MIN_VIEWPORT_PX ||
  (coarsePointer && Math.min(width, height) < CLASSIC_MIN_VIEWPORT_PX);

/**
 * 端末区分 (`room.join.device`, pen-usage-analytics-2026-08-18.md §11.2 / §11.3) を
 * 決める純関数. **判定は `isPhoneViewport` と同じ `PhoneViewportInput` (= layout の
 * スマホ強制 Modern 判定を実際に駆動している値) から取る** — 別途 `innerWidth` から
 * 計算し直すと `device='phone'` なのに `layout='classic'` の矛盾行が出うるため
 * (§11.3 の 5)。呼び出し側は入室時に 1 回だけ `readPhoneViewportInput()` を読んで
 * これに渡す想定 (回転・リサイズは追わない、入室時点のスナップショット).
 *
 * 定義は「端末そのもの」ではなく「**入室時点で出していたシェル + primary
 * pointer**」(`@oekaki/protocol` の `shared/device.ts` 参照):
 *   - `phone`   : `isPhoneViewport` が true (= 実際にフォンシェルを表示中).
 *                 幅 < 640 の PC 細窓や iPad の Split View も phone に数える.
 *   - `tablet`  : coarse pointer (= タッチ主体) かつ phone でない
 *                 (例: iPad 1024×768 → tablet、タッチ対応 PC モニタ 1920×1080 も
 *                 短辺 ≥ 640 で phone でないため tablet 扱い).
 *   - `desktop` : それ以外 (primary pointer が fine な PC).
 */
export const deriveDeviceKind = (input: PhoneViewportInput): DeviceKind => {
  if (isPhoneViewport(input)) return 'phone';
  if (input.coarsePointer) return 'tablet';
  return 'desktop';
};

export interface ResolveLayoutInput {
  /** localStorage に保存済みのユーザ選択 (無ければ store が default = modern を返す). */
  readonly stored: LayoutKind;
  /** スマホか (= `isPhoneViewport(...)`。幅 + coarse pointer × 短辺). */
  readonly isMobile: boolean;
}

/**
 * 実際に表示するレイアウトを解決する純関数 (スライス1 の最小チェーン).
 *
 *   - スマホ → **Modern 固定** (stored を無視、§9.4.1 ④)。
 *   - それ以外 → stored (= ユーザ設定 localStorage > グローバル既定 Modern。
 *     既定解決は `layoutPreference` store 側で済んでいるのでここは stored をそのまま)。
 *
 * 部屋デフォルト・URL ヒントは後続スライスでこの入力に層を足す形で拡張する
 * (= この関数のシグネチャを壊さずに `roomDefault` / `urlHint` を追加できる)。
 */
export const resolveLayout = (input: ResolveLayoutInput): LayoutKind => {
  if (input.isMobile) return 'modern';
  return input.stored;
};

/**
 * 切替トグルを表示してよいか (= スマホでは非表示)。§9.4.1 ④「選べないものを見せない」。
 */
export const shouldShowLayoutToggle = (isMobile: boolean): boolean => !isMobile;

/** `room.join` に載せる telemetry ペア (pen-usage-analytics-2026-08-18.md §11.2/§11.3)。 */
export interface JoinTelemetry {
  /** **表示中の** レイアウト (= `resolveLayout` の結果。stored の生値ではない). */
  readonly layout: LayoutKind;
  readonly device: DeviceKind;
}

/**
 * `room.join.layout` / `room.join.device` を**同一の viewport スナップショット**
 * から導出する. 2026-09-27 fable-advisor レビュー後の修正: 当初 `layout` に
 * `layoutPreferenceStore.get()` (= 保存された好み) をそのまま送っていたが、
 * これは `resolveLayout` を通していない**未解決の値**だった。PC 細窓 / iPad
 * Split View 等で「好みは classic だがスマホ強制で実際は Modern 表示」という
 * ケースでは `device='phone'` かつ `layout='classic'` という矛盾行が出てしまう
 * (`isPhoneViewport` の呼び出しが `deriveDeviceKind` 側にしかなく、`layout` 側は
 * 素の `stored` だったため)。
 *
 * この関数は `isMobile` を 1 回だけ計算し、`layout` (= `resolveLayout` を通した
 * **表示中の値**) と `device` の両方をそこから出す — 呼び出し側 (join /
 * resume-fail fallback join) が別々に `isPhoneViewport` / `deriveDeviceKind` を
 * 呼んで二重実装・ズレを作らないようにするための唯一の入口。
 */
export const resolveJoinTelemetry = (
  input: PhoneViewportInput,
  stored: LayoutKind,
): JoinTelemetry => {
  const isMobile = isPhoneViewport(input);
  return {
    layout: resolveLayout({ stored, isMobile }),
    device: deriveDeviceKind(input),
  };
};
